import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Scan, Check, Package, Trash2, Clock, Truck, Inbox, CheckCircle, RefreshCw, Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import * as validationService from '@/services/validationService';
import { useOperationDraft } from '@/hooks/useOperationDraft';
import { DraftsMenu } from '@/components/DraftsMenu';
import { ResumedDraftBanner } from '@/components/ResumedDraftBanner';

import { EwbDocPanel } from '@/components/ewayBill/EwbDocPanel';
const FROM_SECTION = 'Greige Storage';

const InternalTransfer = () => {
  const navigate = useNavigate();
  const [staffId, setStaffId] = useState('');
  const [staffVerified, setStaffVerified] = useState(false);
  const [staffName, setStaffName] = useState('');
  
  const [sections, setSections] = useState([]);
  const [targetSection, setTargetSection] = useState('');
  const [isRework, setIsRework] = useState(false);
  const [reworkReason, setReworkReason] = useState('');
  const [remarks, setRemarks] = useState('');
  
  const [bundleBarcode, setBundleBarcode] = useState('');
  const [scannedBundles, setScannedBundles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [lastTransfer, setLastTransfer] = useState(null);
  
  // Outgoing: Shipment requests FROM Greige Storage (status: requested)
  const [pendingShipments, setPendingShipments] = useState([]);
  const [pendingShipmentConfirmations, setPendingShipmentConfirmations] = useState([]); // From consignments
  const [confirmingShipment, setConfirmingShipment] = useState(null);
  
  // Incoming: Shipped transfers TO Greige Storage (status: shipped)
  const [shippedTransfers, setShippedTransfers] = useState([]);
  const [acceptingTransfer, setAcceptingTransfer] = useState(null);
  
  const [activeTab, setActiveTab] = useState('outgoing');
  const [loading, setLoading] = useState(false);
  const [resumedDraft, setResumedDraft] = useState(null);

  const barcodeRef = useRef(null);

  const {
    drafts, activeDraftId, saving, lastSavedAt,
    autoSave, clearDraft, loadDraft, removeDraft,
  } = useOperationDraft({ moduleKey: `section:${FROM_SECTION}`, operationType: 'internal_transfer' });

  useEffect(() => {
    if (!staffVerified) return;
    autoSave({
      label: `${FROM_SECTION} → ${targetSection || 'destination'}`,
      bundleCount: scannedBundles.length,
      documentId: `${FROM_SECTION}->${targetSection || 'destination'}`,
      documentNo: targetSection || '',
      payload: { staffId, staffName, targetSection, isRework, reworkReason, remarks, scannedBundles },
    });
  }, [scannedBundles, staffVerified, targetSection, isRework, reworkReason, remarks, staffId, staffName, autoSave]);

  const handleResumeDraft = async (draftId) => {
    try {
      const doc = await loadDraft(draftId);
      const p = doc.payload || {};
      setStaffId(p.staffId || '');
      setStaffName(p.staffName || '');
      setStaffVerified(!!p.staffId);
      setTargetSection(p.targetSection || '');
      setIsRework(!!p.isRework);
      setReworkReason(p.reworkReason || '');
      setRemarks(p.remarks || '');
      setScannedBundles(p.scannedBundles || []);
      setActiveTab('outgoing');
      const summary = (drafts || []).find((x) => x.draft_id === draftId) || null;
      setResumedDraft(summary);
      toast.success(summary?.last_updated_by_name ? `Resumed draft — last worked on by ${summary.last_updated_by_name}` : 'Draft restored');
      setTimeout(() => barcodeRef.current?.focus(), 100);
    } catch {
      toast.error('Failed to restore draft');
    }
  };

  const handlePickDestination = (value) => {
    setTargetSection(value);
    if (scannedBundles.length > 0) return;
    const existing = (drafts || []).find((d) => d.document_id === `${FROM_SECTION}->${value}`);
    if (existing) handleResumeDraft(existing.draft_id);
    else setResumedDraft(null);
  };

  const handleStartFresh = async () => {
    const id = resumedDraft?.draft_id;
    if (id) {
      const ok = await removeDraft(id);
      if (!ok) { toast.error('Could not discard the shared draft — please try again'); return; }
    }
    setResumedDraft(null);
    setScannedBundles([]);
    setTargetSection('');
    setRemarks('');
    setIsRework(false);
    setReworkReason('');
  };

  useEffect(() => {
    fetchSections();
    fetchAllPendingShipments();
  }, []);

  const fetchSections = async () => {
    try {
      const response = await apiClient.get('/internal-transfer/sections', {
        params: { exclude_section: FROM_SECTION }
      });
      setSections(response.data.sections || []);
    } catch (error) {
      console.error('Failed to fetch sections:', error);
    }
  };

  const fetchAllPendingShipments = async () => {
    setLoading(true);
    try {
      // 1. Get shipment requests where Greige Storage is the source (status = requested)
      // These are transfers that destination sections have requested and need Greige Storage to ship
      const shipmentRequestsRes = await apiClient.get('/internal-transfer/shipment-requests', {
        params: { from_section: FROM_SECTION }
      });
      setPendingShipments(shipmentRequestsRes.data.transfers || []);
      
      // 2. Check the greige-storage specific endpoint for consignment-based shipments
      try {
        const incomingRes = await apiClient.get('/greige-storage/pending-shipment-confirmations');
        setPendingShipmentConfirmations(incomingRes.data.pending || []);
      } catch (err) {
        setPendingShipmentConfirmations([]);
      }
      
      // 3. Get shipped transfers TO Greige Storage (status: shipped) - need to accept
      const shippedRes = await apiClient.get('/internal-transfer/shipped-transfers', {
        params: { section: FROM_SECTION }
      });
      setShippedTransfers(shippedRes.data.transfers || []);
    } catch (error) {
      console.error('Error fetching pending shipments:', error);
    } finally {
      setLoading(false);
    }
  };

  // Step 6: Accept shipped transfer (destination receives goods)
  const handleAcceptTransfer = async (transferId) => {
    setAcceptingTransfer(transferId);
    try {
      await apiClient.post('/internal-transfer/accept', {
        transfer_id: transferId
      });
      toast.success('Transfer received! Proceed to Bundle Receipt Entry.');
      fetchAllPendingShipments();
    } catch (error) {
      console.error('Error accepting transfer:', error);
      toast.error(error.response?.data?.detail || 'Failed to accept transfer');
    } finally {
      setAcceptingTransfer(null);
    }
  };

  const handleVerifyStaff = async () => {
    if (!staffId.trim()) {
      toast.error('Please enter Staff ID');
      return;
    }
    try {
      const result = await validationService.quickValidateStaff(staffId.trim());
      if (result.valid) {
        setStaffVerified(true);
        setStaffName(result.staffName);
        toast.success(`Staff verified: ${result.staffName}`);
        setTimeout(() => barcodeRef.current?.focus(), 100);
      } else {
        toast.error(result.message || 'Staff not found');
      }
    } catch (error) {
      toast.error('Staff not found');
    }
  };

  const handleScanBundle = async (e) => {
    if (e.key !== 'Enter' || !bundleBarcode.trim()) return;
    
    const barcode = bundleBarcode.trim().toUpperCase();
    
    if (scannedBundles.some(b => b.barcode === barcode)) {
      toast.error('Bundle already scanned');
      setBundleBarcode('');
      return;
    }

    // Validate bundle using the internal transfer validation endpoint
    // This checks both inventory AND pick slips for picked bundles
    try {
      const response = await apiClient.post('/internal-transfer/validate-bundle', null, {
        params: { 
          bundle_code: barcode, 
          from_section: FROM_SECTION,
          to_section: targetSection
        }
      });
      
      if (response.data.valid) {
        // Enrich the scanned bundle with Sort No / Kuri Color / Border / Design Name / SKU / Qty
        let meta = {};
        try {
          const m = await apiClient.get('/greige-storage/bundle-transfer-meta', {
            params: { bundle_barcode: barcode },
          });
          meta = m.data || {};
        } catch (err) {
          // Non-blocking: still add the bundle even if enrichment fails
        }

        // Greige Storage rule: only ONE SKU (FG code) per transfer.
        // Compare against the SKU already established by earlier scans.
        const newSku = (meta.sku_code || '').trim();
        const existingSku = (scannedBundles.find(b => (b.sku_code || '').trim())?.sku_code || '').trim();
        if (newSku && existingSku && newSku !== existingSku) {
          const existingDesign = scannedBundles.find(b => (b.sku_code || '').trim() === existingSku)?.design_name || '';
          toast.error(
            `This transfer is for SKU ${existingSku}${existingDesign ? ` (${existingDesign})` : ''}. ` +
            `Bundle ${barcode} is a different SKU ${newSku}${meta.design_name ? ` (${meta.design_name})` : ''} — ` +
            `start a new transfer for it.`
          );
          setBundleBarcode('');
          setTimeout(() => barcodeRef.current?.focus(), 50);
          return;
        }

        setScannedBundles(prev => [{
          barcode,
          sort_no: meta.sort_no || '',
          color_name: meta.color_name || '',
          border: meta.border || '',
          design_name: meta.design_name || '',
          sku_code: newSku,
          size: meta.size || '',
          warp_count: meta.warp_count || '',
          weft_count: meta.weft_count || '',
          reed: meta.reed || '',
          pick: meta.pick || '',
          width: meta.width || '',
          inspection_width: meta.inspection_width ?? '',
          bundle_weight: meta.bundle_weight ?? '',
          qty: meta.qty ?? 0,
          qty_unit: meta.qty_unit || 'Pc',
        }, ...prev]);
        toast.success('Bundle added');
        setBundleBarcode('');
        setTimeout(() => barcodeRef.current?.focus(), 50);
      } else {
        toast.error(response.data.error || `Bundle ${barcode} not available for transfer`);
        setBundleBarcode('');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || `Bundle ${barcode} not found in Greige Storage`);
      setBundleBarcode('');
    }
  };

  const handleRemoveBundle = (barcode) => {
    setScannedBundles(prev => prev.filter(b => b.barcode !== barcode));
    toast.info(`Bundle ${barcode} removed`);
  };

  const handleConfirmShipment = async (transferId, isIncomingRequest = false) => {
    setConfirmingShipment(transferId);
    try {
      if (isIncomingRequest) {
        // Use the new Greige Storage specific endpoint for incoming requests
        await apiClient.post(`/greige-storage/confirm-shipment/${encodeURIComponent(transferId)}`);
      } else {
        // Use the standard internal transfer endpoint for outgoing transfers
        await apiClient.post('/internal-transfer/confirm-shipment', {
          transfer_id: transferId
        });
      }
      toast.success('Shipment confirmed successfully');
      fetchAllPendingShipments();
    } catch (error) {
      console.error('Error confirming shipment:', error);
      toast.error(error.response?.data?.detail || 'Failed to confirm shipment');
    } finally {
      setConfirmingShipment(null);
    }
  };

  const handleSubmit = async () => {
    if (!targetSection) {
      toast.error('Please select target section');
      return;
    }
    if (scannedBundles.length === 0) {
      toast.error('Please scan at least one bundle');
      return;
    }
    
    try {
      setSubmitting(true);
      const isReworkSubmit = isRework && (targetSection || '').toLowerCase().includes('inhouse');
      if (isReworkSubmit && !reworkReason.trim()) {
        toast.error('Provide a rework reason');
        setSubmitting(false);
        return;
      }

      // Use the common internal transfer create API
      const response = await apiClient.post('/internal-transfer/create', {
        staff_id: staffId,
        to_section: targetSection,
        bundle_codes: scannedBundles.map(b => b.barcode),
        from_section: FROM_SECTION,
        transfer_type: isReworkSubmit ? 'REWORK' : 'STANDARD',
        rework_reason: isReworkSubmit ? reworkReason.trim() : undefined,
        remarks: remarks.trim() || undefined,
      });

      toast.success(response.data.message);

      // Snapshot the just-made transfer so the operator can print the sheet.
      setLastTransfer({
        transfer_id: response.data.transfer_id,
        from_section: FROM_SECTION,
        to_section: targetSection,
        staff: staffName ? `${staffName} (${staffId})` : staffId,
        date: new Date().toLocaleString(),
        bundles: scannedBundles,
      });

      // Reset form
      setScannedBundles([]);
      setTargetSection('');
      setRemarks('');
      setIsRework(false);
      setReworkReason('');
      clearDraft();
      setResumedDraft(null);
      
      // Show transfer ID
      setTimeout(() => {
        toast.success(`Transfer ID: ${response.data.transfer_id}`);
      }, 500);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transfer failed');
    } finally {
      setSubmitting(false);
    }
  };

  const printTransfer = (t) => {
    if (!t) return;
    const groups = {};
    (t.bundles || []).forEach((b) => {
      const key = b.sort_no || '—';
      if (!groups[key]) {
        groups[key] = {
          sort_no: key, border: b.border || '', size: b.size || '',
          warp_count: b.warp_count || '', weft_count: b.weft_count || '',
          reed: b.reed || '', pick: b.pick || '', width: b.width || '', qty: 0,
        };
      }
      groups[key].qty += Number(b.qty || 0);
      ['border', 'size', 'warp_count', 'weft_count', 'reed', 'pick', 'width'].forEach((f) => {
        if (!groups[key][f] && b[f]) groups[key][f] = b[f];
      });
    });
    const rows = Object.values(groups).sort((a, b) =>
      String(a.sort_no).localeCompare(String(b.sort_no), undefined, { numeric: true }));
    const totalQty = rows.reduce((s, r) => s + Number(r.qty || 0), 0);
    const esc = (v) => String(v ?? '').replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
    const bodyRows = rows.map((r) => `<tr>
        <td>${esc(r.sort_no)}</td><td>${esc(r.border)}</td><td>${esc(r.size)}</td>
        <td class="c">${esc(r.warp_count)}</td><td class="c">${esc(r.weft_count)}</td>
        <td class="c">${esc(r.reed)}</td><td class="c">${esc(r.pick)}</td><td class="c">${esc(r.width)}</td>
        <td class="c b">${esc(r.qty)}</td></tr>`).join('');
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Internal Transfer ${esc(t.transfer_id)}</title>
      <style>
        * { font-family: Arial, Helvetica, sans-serif; }
        body { margin: 24px; color: #111; }
        h1 { font-size: 18px; margin: 0 0 2px; }
        .sub { font-size: 12px; color: #555; margin-bottom: 14px; }
        table.meta { width: 100%; font-size: 12px; margin-bottom: 14px; border-collapse: collapse; }
        table.meta td { padding: 2px 6px; }
        table.meta .k { color: #666; width: 90px; }
        table.grid { width: 100%; border-collapse: collapse; font-size: 12px; }
        table.grid th, table.grid td { border: 1px solid #333; padding: 6px 8px; text-align: left; }
        table.grid th { background: #f0f0f0; }
        td.c, th.c { text-align: center; }
        .b { font-weight: bold; }
        tfoot td { font-weight: bold; background: #fafafa; }
        @media print { body { margin: 10mm; } }
      </style></head><body>
      <h1>Internal Transfer Sheet</h1>
      <div class="sub">Minister White — Greige Storage</div>
      <table class="meta">
        <tr><td class="k">Transfer ID</td><td><b>${esc(t.transfer_id || '-')}</b></td>
            <td class="k">Date</td><td>${esc(t.date)}</td></tr>
        <tr><td class="k">From</td><td>${esc(t.from_section)}</td>
            <td class="k">To</td><td>${esc(t.to_section)}</td></tr>
        <tr><td class="k">Staff</td><td>${esc(t.staff)}</td>
            <td class="k">Sort Nos</td><td>${rows.length}</td></tr>
      </table>
      <table class="grid">
        <thead><tr>
          <th>Sort No</th><th>Border</th><th>Size</th><th class="c">Warp Count</th>
          <th class="c">Weft Count</th><th class="c">Reed</th><th class="c">Pick</th>
          <th class="c">Width</th><th class="c">Qty</th>
        </tr></thead>
        <tbody>${bodyRows}</tbody>
        <tfoot><tr><td colspan="8" style="text-align:right">Total Qty</td><td class="c">${totalQty}</td></tr></tfoot>
      </table>
      </body></html>`;
    const win = window.open('', '_blank');
    if (!win) { toast.error('Popup blocked — allow popups to print the sheet'); return; }
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 300);
  };

  const printBundleWise = (t) => {
    if (!t) return;
    const esc = (v) => String(v ?? '').replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
    const rows = [...(t.bundles || [])].sort((a, b) =>
      String(a.barcode).localeCompare(String(b.barcode), undefined, { numeric: true }));
    const totalQty = rows.reduce((s, r) => s + Number(r.qty || 0), 0);
    const totalWeight = rows.reduce((s, r) => s + (Number(r.bundle_weight) || 0), 0);
    const anyWeight = rows.some((r) => r.bundle_weight !== '' && r.bundle_weight != null);
    const bodyRows = rows.map((r, i) => `<tr>
        <td class="c">${i + 1}</td>
        <td>${esc(r.barcode)}</td>
        <td>${esc(r.color_name)}</td>
        <td class="c b">${esc(r.qty ?? 0)}</td>
        <td class="c">${esc(r.inspection_width !== '' && r.inspection_width != null ? r.inspection_width : '')}</td>
        <td class="c">${esc(r.bundle_weight !== '' && r.bundle_weight != null ? r.bundle_weight : '')}</td>
      </tr>`).join('');
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Bundle-Wise ${esc(t.transfer_id)}</title>
      <style>
        * { font-family: Arial, Helvetica, sans-serif; }
        body { margin: 24px; color: #111; }
        h1 { font-size: 18px; margin: 0 0 2px; }
        .sub { font-size: 12px; color: #555; margin-bottom: 14px; }
        table.meta { width: 100%; font-size: 12px; margin-bottom: 14px; border-collapse: collapse; }
        table.meta td { padding: 2px 6px; }
        table.meta .k { color: #666; width: 90px; }
        table.grid { width: 100%; border-collapse: collapse; font-size: 12px; }
        table.grid th, table.grid td { border: 1px solid #333; padding: 6px 8px; text-align: left; }
        table.grid th { background: #f0f0f0; }
        td.c, th.c { text-align: center; }
        .b { font-weight: bold; }
        tfoot td { font-weight: bold; background: #fafafa; }
        @media print { body { margin: 10mm; } }
      </style></head><body>
      <h1>Bundle-Wise Transfer Sheet</h1>
      <div class="sub">Minister White — Greige Storage</div>
      <table class="meta">
        <tr><td class="k">Transfer ID</td><td><b>${esc(t.transfer_id || '-')}</b></td>
            <td class="k">Date</td><td>${esc(t.date)}</td></tr>
        <tr><td class="k">From</td><td>${esc(t.from_section)}</td>
            <td class="k">To</td><td>${esc(t.to_section)}</td></tr>
        <tr><td class="k">Staff</td><td>${esc(t.staff)}</td>
            <td class="k">Bundles</td><td>${rows.length}</td></tr>
      </table>
      <table class="grid">
        <thead><tr>
          <th class="c">#</th><th>Bundle No</th><th>Color</th>
          <th class="c">Qty</th><th class="c">Width</th><th class="c">Weight (kg)</th>
        </tr></thead>
        <tbody>${bodyRows}</tbody>
        <tfoot><tr><td colspan="3" style="text-align:right">Total</td><td class="c">${totalQty}</td><td></td><td class="c">${anyWeight ? totalWeight.toFixed(2) : ''}</td></tr></tfoot>
      </table>
      </body></html>`;
    const win = window.open('', '_blank');
    if (!win) { toast.error('Popup blocked — allow popups to print the sheet'); return; }
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 300);
  };


  const handleReset = () => {
    setStaffVerified(false);
    setStaffId('');
    setStaffName('');
    setScannedBundles([]);
    setTargetSection('');
    setRemarks('');
    setResumedDraft(null);
  };

  // Count for badges
  const outgoingCount = pendingShipments.length + pendingShipmentConfirmations.length;
  const incomingCount = shippedTransfers.length;

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/sections/greige-storage')} 
              className="text-[#F9FAFB] hover:bg-white/10"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Internal Transfer</h1>
              <p className="text-sm text-[#E8DCC8]">Greige Storage</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <DraftsMenu
              drafts={drafts}
              onResume={handleResumeDraft}
              onDelete={removeDraft}
              activeDraftId={activeDraftId}
              saving={saving}
              lastSavedAt={lastSavedAt}
              variant="dark"
            />
            <Button 
              variant="outline" 
              size="sm"
              onClick={fetchAllPendingShipments}
              className="border-white/20 text-white hover:bg-white/10"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-8">
        {/* Pending Shipment Confirmations from Other Sections - HIGH PRIORITY */}
        {pendingShipmentConfirmations.length > 0 && (
          <Card className="mb-6 border-red-200 bg-red-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-700">
                <Inbox className="w-5 h-5" />
                Shipment Requests ({pendingShipmentConfirmations.length})
              </CardTitle>
              <CardDescription className="text-red-600">
                Other sections have requested bundles FROM Greige Storage - confirm shipment to send
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {pendingShipmentConfirmations.map((request) => (
                  <div 
                    key={request.entry_id} 
                    className="flex items-center justify-between p-4 bg-white rounded-lg border border-red-200"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="bg-red-100 text-red-700 border-red-300">
                          {request.document_barcode || request.entry_id}
                        </Badge>
                        <span className="text-sm text-gray-600">→</span>
                        <Badge className="bg-purple-100 text-purple-700 border-0">
                          {request.section_name || request.to_section || 'Unknown'}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        {request.bundle_count || request.bundles?.length || 0} bundles • Requested by {request.requested_by_name || 'Section'}
                      </p>
                      <p className="text-xs text-gray-400">
                        Requested: {request.requested_at ? new Date(request.requested_at).toLocaleString() : 'N/A'}
                      </p>
                    </div>
                    <Button
                      onClick={() => handleConfirmShipment(request.entry_id, true)}
                      disabled={confirmingShipment === request.entry_id}
                      className="bg-green-600 hover:bg-green-700 text-white"
                      data-testid={`confirm-incoming-${request.entry_id}`}
                    >
                      {confirmingShipment === request.entry_id ? (
                        <>Confirming...</>
                      ) : (
                        <>
                          <Truck className="w-4 h-4 mr-2" />
                          Confirm Shipment
                        </>
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pending Outgoing Shipments - from transfers Greige Storage initiated */}
        {pendingShipments.length > 0 && (
          <Card className="mb-6 border-orange-200 bg-orange-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-orange-700">
                <Clock className="w-5 h-5" />
                Pending Outgoing Shipments ({pendingShipments.length})
              </CardTitle>
              <CardDescription>Transfers accepted by receiving sections - confirm to ship</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {pendingShipments.map((shipment) => (
                  <div 
                    key={shipment.transfer_id} 
                    className="flex items-center justify-between p-4 bg-white rounded-lg border border-orange-200"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="bg-orange-100 text-orange-700 border-orange-300">
                          {shipment.transfer_id}
                        </Badge>
                        <span className="text-sm text-gray-600">→</span>
                        <Badge className="bg-purple-100 text-purple-700 border-0">
                          {shipment.to_section}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        {shipment.bundle_count || shipment.bundle_codes?.length || 0} bundles • Accepted by {shipment.accepted_by_name || 'Section'}
                      </p>
                      <p className="text-xs text-gray-400">
                        Requested: {new Date(shipment.requested_at).toLocaleString()}
                      </p>
                    </div>
                    <Button
                      onClick={() => handleConfirmShipment(shipment.transfer_id, false)}
                      disabled={confirmingShipment === shipment.transfer_id}
                      className="bg-green-600 hover:bg-green-700 text-white"
                      data-testid={`confirm-shipment-${shipment.transfer_id}`}
                    >
                      {confirmingShipment === shipment.transfer_id ? (
                        <>Confirming...</>
                      ) : (
                        <>
                          <Truck className="w-4 h-4 mr-2" />
                          Confirm Shipment
                        </>
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Staff Verification */}
        {!staffVerified ? (
          <Card className="border-[#E5DFD0]">
            <CardHeader>
              <CardTitle>Staff Verification</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="staffId">Staff ID</Label>
                  <Input
                    id="staffId"
                    value={staffId}
                    onChange={(e) => setStaffId(e.target.value)}
                    placeholder="Enter Staff ID"
                    onKeyDown={(e) => e.key === 'Enter' && handleVerifyStaff()}
                    data-testid="staff-id-input"
                  />
                </div>
                <Button
                  onClick={handleVerifyStaff}
                  className="w-full bg-[#2D5F4F]"
                  data-testid="verify-staff-button"
                >
                  Verify Staff
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Verified Staff Display */}
            <Card className="border-green-200 bg-green-50">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="font-medium text-green-800">{staffName}</p>
                      <p className="text-sm text-green-600">ID: {staffId}</p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={handleReset} className="text-red-600">
                    Change
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Just-submitted transfer — print the sheet */}
            {lastTransfer && (
              <Card className="border-green-300 bg-green-50" data-testid="transfer-success-card">
                <CardContent className="py-4 flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <p className="font-semibold text-green-800 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" /> Transfer submitted
                    </p>
                    <p className="text-sm text-green-700 mt-0.5">
                      ID: <span className="font-mono" data-testid="transfer-success-id">{lastTransfer.transfer_id}</span>
                      {' · '}{lastTransfer.bundles.length} bundles → {lastTransfer.to_section}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => printTransfer(lastTransfer)}
                      className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
                      data-testid="print-transfer-btn"
                    >
                      <Printer className="w-4 h-4 mr-2" /> Print Transfer Sheet
                    </Button>
                    <Button
                      onClick={() => printBundleWise(lastTransfer)}
                      variant="outline"
                      className="border-[#2D5F4F] text-[#2D5F4F] hover:bg-[#2D5F4F]/10"
                      data-testid="print-bundlewise-btn"
                    >
                      <Printer className="w-4 h-4 mr-2" /> Print Bundle-Wise
                    </Button>
                    <Button variant="outline" onClick={() => setLastTransfer(null)} data-testid="dismiss-transfer-btn">
                      Dismiss
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}


            {/* Transfer Form */}
            {resumedDraft && (
              <ResumedDraftBanner draft={resumedDraft} onStartFresh={handleStartFresh} />
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left - Form */}
              <div className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Target Section</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Select value={targetSection} onValueChange={handlePickDestination}>
                      <SelectTrigger data-testid="target-section-select">
                        <SelectValue placeholder="Select destination" />
                      </SelectTrigger>
                      <SelectContent>
                        {sections.map((s) => (
                          <SelectItem key={s.section_name} value={s.section_name}>
                            {s.section_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {(targetSection || '').toLowerCase().includes('inhouse') && (
                      <div className="mt-4 rounded border border-amber-300 bg-amber-50 p-3 text-sm">
                        <label className="flex items-center gap-2 font-medium text-amber-900 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isRework}
                            onChange={(e) => setIsRework(e.target.checked)}
                            data-testid="rework-toggle"
                            className="h-4 w-4 accent-amber-600"
                          />
                          Send for Rework / Re-Assortment
                        </label>
                        {isRework && (
                          <>
                            <div className="mt-2 text-xs text-amber-800">
                              On receipt at Inhouse, these bundles will be <b>fully retired</b>
                              and their USNs released into the Rework Pool.
                            </div>
                            <Input
                              value={reworkReason}
                              onChange={(e) => setReworkReason(e.target.value)}
                              placeholder="Reason for rework"
                              className="mt-2 bg-white"
                              data-testid="rework-reason-input"
                            />
                          </>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Scan Bundles</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Input
                      ref={barcodeRef}
                      value={bundleBarcode}
                      onChange={(e) => setBundleBarcode(e.target.value)}
                      onKeyDown={handleScanBundle}
                      placeholder="Scan bundle barcode"
                      data-testid="bundle-barcode-input"
                    />
                    <p className="text-xs text-gray-500 mt-2">Press Enter after scanning</p>
                  </CardContent>
                </Card>

                {scannedBundles.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Remarks (Optional)</CardTitle>
                      <CardDescription>Note for the receiving section</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Textarea
                        value={remarks}
                        onChange={(e) => setRemarks(e.target.value)}
                        placeholder="e.g. handle this lot first, urgent for shipment…"
                        rows={2}
                        data-testid="remarks-input"
                      />
                    </CardContent>
                  </Card>
                )}

                {scannedBundles.length > 0 && (
                  <Button 
                    onClick={handleSubmit} 
                    disabled={submitting || !targetSection}
                    className="w-full bg-[#2D5F4F] hover:bg-[#234A3D]"
                    data-testid="submit-transfer-btn"
                  >
                    {submitting ? (
                      'Processing...'
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Submit Transfer Request ({scannedBundles.length} bundles)
                      </>
                    )}
                  </Button>
                )}
              </div>

              {/* Right - Scanned List */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    Scanned Bundles ({scannedBundles.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {scannedBundles.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      <Scan className="w-12 h-12 mx-auto mb-2 opacity-30" />
                      <p>No bundles scanned</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-[28rem] overflow-y-auto">
                      {scannedBundles.map((b, idx) => (
                        <div
                          key={b.barcode}
                          className="p-3 bg-gray-50 rounded border border-gray-200"
                          data-testid={`scanned-bundle-${b.barcode}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="text-xs text-gray-400">#{idx + 1}</span>
                              <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
                              <span className="font-mono text-sm font-semibold truncate" data-testid={`scanned-bundle-code-${b.barcode}`}>{b.barcode}</span>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveBundle(b.barcode)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0 shrink-0"
                              data-testid={`remove-scanned-bundle-${b.barcode}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs pl-6">
                            <div className="flex justify-between gap-2">
                              <span className="text-gray-500">Sort No</span>
                              <span className="font-medium text-gray-800 text-right" data-testid={`scanned-sortno-${b.barcode}`}>{b.sort_no || '—'}</span>
                            </div>
                            <div className="flex justify-between gap-2">
                              <span className="text-gray-500">Qty</span>
                              <span className="font-semibold text-[#2D5F4F] text-right" data-testid={`scanned-qty-${b.barcode}`}>{b.qty ?? 0}{b.qty_unit === 'Mtr' ? ' Mtr' : ''}</span>
                            </div>
                            <div className="flex justify-between gap-2">
                              <span className="text-gray-500">Kuri Color</span>
                              <span className="font-medium text-gray-800 text-right" data-testid={`scanned-color-${b.barcode}`}>{b.color_name || '—'}</span>
                            </div>
                            <div className="flex justify-between gap-2">
                              <span className="text-gray-500">Border</span>
                              <span className="font-medium text-gray-800 text-right" data-testid={`scanned-border-${b.barcode}`}>{b.border || '—'}</span>
                            </div>
                            <div className="flex justify-between gap-2 col-span-2">
                              <span className="text-gray-500">Design Name</span>
                              <span className="font-medium text-gray-800 text-right" data-testid={`scanned-design-${b.barcode}`}>{b.design_name || '—'}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Summary */}
            {scannedBundles.length > 0 && targetSection && (
              <Card className="border-blue-200 bg-blue-50">
                <CardContent className="py-4">
                  <div className="flex items-center justify-between text-sm">
                    <div>
                      <span className="text-gray-600">From:</span>
                      <span className="font-semibold ml-2">{FROM_SECTION}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">→ To:</span>
                      <span className="font-semibold ml-2 text-purple-700">{targetSection}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Bundles:</span>
                      <span className="font-bold ml-2 text-blue-700">{scannedBundles.length}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Incoming Shipped Transfers - need to accept (Step 5 & 6) */}
        {shippedTransfers.length > 0 && (
          <Card className="mt-6 border-blue-200 bg-blue-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-blue-700">
                <Truck className="w-5 h-5" />
                Incoming Shipments ({shippedTransfers.length})
              </CardTitle>
              <CardDescription>Shipped transfers waiting for you to receive and accept</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {shippedTransfers.map((transfer) => (
                  <div 
                    key={transfer.transfer_id} 
                    className="flex items-center justify-between p-4 bg-white rounded-lg border border-blue-200"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-300">
                          {transfer.transfer_id}
                        </Badge>
                        <span className="text-sm text-gray-600">from</span>
                        <Badge className="bg-indigo-100 text-indigo-700 border-0">
                          {transfer.from_section}
                        </Badge>
                        <Badge className="bg-green-100 text-green-700 border-0">
                          <Truck className="w-3 h-3 mr-1" />
                          Shipped
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        {transfer.bundle_count || transfer.bundle_codes?.length || 0} bundles
                      </p>
                      <p className="text-xs text-gray-400">
                        Shipped by {transfer.shipped_by_name || 'Unknown'} on {transfer.shipped_at ? new Date(transfer.shipped_at).toLocaleString() : 'N/A'}
                      </p>
                    </div>
                    <Button
                      onClick={() => handleAcceptTransfer(transfer.transfer_id)}
                      disabled={acceptingTransfer === transfer.transfer_id}
                      className="bg-green-600 hover:bg-green-700 text-white"
                      data-testid={`accept-transfer-${transfer.transfer_id}`}
                    >
                      {acceptingTransfer === transfer.transfer_id ? (
                        <>Accepting...</>
                      ) : (
                        <>
                          <Check className="w-4 h-4 mr-2" />
                          Accept & Receive
                        </>
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        <div className="mt-6"><EwbDocPanel docType="branch_transfer" section={FROM_SECTION} challan title="Internal transfer e-way bills (goods leaving the site)" /></div>
      </main>
    </div>
  );
};

export default InternalTransfer;
